import { useState, useEffect, useContext, useRef } from "react";
import { getClaims, getToken } from "../components/Auth/handleJWT";
import {
	HttpTransportType,
	HubConnection,
	HubConnectionBuilder,
} from "@microsoft/signalr";
import { claim } from "../components/Auth/auth.models";
import { useAlert } from "../components/Alert/Alerts";
import { Alert } from "../components/Alert/types";
import { RootState } from "../store";
import { useSelector } from "react-redux";

export type Notification = {
	id: string;
	type: string;
	action: NotificationAction;
	data: any;
};

export enum NotificationAction {
	Notification,
	Add,
	Delete,
	Update,
}

export type UseNotificationHubReturnType = {
	connection: HubConnection | null;
	hubNotifications: Array<Notification>;
	sendNotification: (groupName: string, notification: Notification) => void;
	joinGroup: (groupName: string, connection: HubConnection) => void;
	leaveGroup: (groupName: string, connection: HubConnection) => void;
	isConnected: boolean;
};

export function useNotificationHub(
	claims: claim[]
): UseNotificationHubReturnType {
	const { addNewAlert, removeAlert } = useAlert();
	const { settings } = useSelector((state: RootState) => state.settings);
	const [connection, setConnection] = useState<HubConnection | null>(null);
	const [hubNotifications, setHubNotifications] = useState<Array<Notification>>(
		[]
	);
	const [isConnected, setIsConnected] = useState<boolean>(false);
	const [reconnectingAlert, setReconnectingAlert] = useState<Alert | null>(
		null
	);
	const [isReconnecting, setIsReconnecting] = useState<boolean | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const userId = claims.find((claim) => claim.name === "userId")?.value;

	useEffect(() => {
		if (userId) {
			const createConnection = async () => {
				const hubConnection = new HubConnectionBuilder()
					.withUrl(`${process.env.REACT_APP_BACKEND_URL}/notificationHub`, {
						accessTokenFactory: () => getToken() || "",
						transport: HttpTransportType.WebSockets,
					})
					.withAutomaticReconnect([
						0, 2000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
						10000,
					])
					.build();

				// hubConnection.on("ReceiveNotification", (notification: Notification) => {
				//   console.log("Got a Notification", notification);

				//   setNotifications((prev) => [...prev, notification]);
				// });

				hubConnection.onreconnecting(() => {
					setIsConnected(false);
					setIsReconnecting(true);
				});

				hubConnection.onreconnected(() => {
					setIsConnected(true);
					setIsReconnecting(false);
				});
				hubConnection.onclose(() => setIsConnected(false));

				try {
					await hubConnection.start();
					setIsConnected(true);
				} catch (error) {
					console.error("Error starting SignalR connection: ", error);
				}

				setConnection(hubConnection);
			};

			createConnection();

			// Cleanup on component unmount
			return () => {
				if (connection) {
					connection.stop();
				}
			};
		}
	}, [userId]);

	const sendNotification = async (
		groupName: string,
		notification: Notification
	) => {
		if (connection) {
			try {
				await connection.invoke(
					"SendNotificationToGroup",
					groupName,
					notification
				);
			} catch (error) {
				console.error("Error sending notification: ", error);
			}
		}
	};

	const joinGroup = async (groupName: string, connection: HubConnection) => {
		if (connection) {
			try {
				await connection.invoke("JoinNotificationGroup", groupName);
			} catch (error) {
				console.error("Error joining group: ", error);
			}
		}
	};

	const leaveGroup = async (groupName: string, connection: HubConnection) => {
		if (connection) {
			try {
				await connection.invoke("LeaveNotificationGroup", groupName);
			} catch (error) {
				console.error("Error leaving group: ", error);
			}
		}
	};

	useEffect(() => {
		if (
			isReconnecting !== null &&
			settings.featureFlags["Notification Hub Alerts"]
		) {
			if (isReconnecting) {
				// Show reconnecting alert after 10 second buffer
				timeoutRef.current = setTimeout(() => {
					const alert = addNewAlert({
						type: "error",
						message:
							"Disconnected from Messages & Notifications. Reconnecting...",
						autoHide: false,
					});

					setReconnectingAlert(alert);
				}, 10000);
			} else {
				// hide reconnecting alert
				if (reconnectingAlert) {
					removeAlert(reconnectingAlert.id);
					setReconnectingAlert(null);

					addNewAlert({
						type: "success",
						message: "Reconnected to Messages & Notifications",
					});
				}

				// clear timer so reconnecting alert doesn't show
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			}
		}
	}, [isReconnecting]);

	return {
		connection,
		hubNotifications: hubNotifications,
		sendNotification,
		joinGroup,
		leaveGroup,
		isConnected,
	};
}
